import type { ReactNode } from "react";

// Statuses where a sale has been accepted by the seller and is progressing through
// post-sale steps. Used to decide when the buyer should see "Godtatt" on the card.
// This is an explicit allowlist — any new status must be added here consciously.
export const ACCEPTED_SALE_STATUSES = [
  "sold",
  "contract_pending",
  "invoice_pending",
  "payment_pending",
  "ready_for_pickup",
  "picked_up",
  "in_transit",
  "delivered",
  "complaint_review",
  "pending_payout",
  "paid_out",
] as const;

export type UnifiedAuction = {
  id: string;
  referenceNumber: string;
  title: string;
  status: string;
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt: string;
  closedAt?: string | null;
  type?: "regular" | "flash" | null;
  durationMinutes?: number | null;
  currentBid: number;
  startingBid: number;
  minimumIncrement: number;
  bidCount: number;
  finalPrice?: number;
  sellerOrganizationId?: string;
  highestBidderOrganizationId?: string | null;
  myHighestBid?: number | null;
  isOutbid?: boolean | null;
  sellerPayout: number;
  buyerPrice: number;
  primaryImage?: {
    url: string;
    thumbnailUrl?: string | null;
    altText?: string | null;
  } | null;
  vehicle: {
    id: string;
    make: string;
    model: string;
    variant?: string | null;
    modelYear?: number | null;
    mileage?: number | null;
    registrationNumber?: string | null;
    fuelType?: string | null;
    transmission?: string | null;
  } | null;
  offers?: Array<{
    id: string;
    round: number;
    offeredBy: string;
    amount: number;
    notes: string | null;
    status: string;
    createdAt: string;
    sellerPayout: number;
    buyerPrice: number;
  }>;
  signedContractId?: string | null;
  transportOrdered?: boolean;
  invoices: Array<{ id: string; documentNumber: string | null; documentType: string }>;
};
import { useNavigate } from "react-router-dom";
import { Heart, Clock, Gavel, Zap } from "lucide-react";
import {
  formatMileage,
  normalizeFuelType,
  normalizeTransmission,
} from "../../lib/auction-utils";
import { LiveTimer } from "./LiveTimer";

export type AuctionVehicle = {
  id: string;
  make: string;
  model: string;
  variant?: string | null;
  modelYear?: number | null;
  mileage?: number | null;
  registrationNumber?: string | null;
  fuelType?: string | null;
  transmission?: string | null;
  wheelDrive?: string | null;
  exteriorColor?: string | null;
};

export type AuctionImage = {
  url: string;
  thumbnailUrl?: string | null;
  altText?: string | null;
};

// Shared card wrapper with image + content layout
export function AuctionCardWrapper({
  onClick,
  className = "",
  style,
  children,
}: {
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      className={`group flex flex-col lg:flex-row overflow-hidden rounded-2xl bg-white transition-all hover:shadow-xl active:scale-[0.98] ${className}`}
      style={{
        border: "1px solid var(--border)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        cursor: "pointer",
        ...style,
      }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// Image section with overlay slots
export function AuctionCardImage({
  image,
  title,
  topLeft,
  topRight,
  bottomRight,
}: {
  image?: AuctionImage | null;
  title: string;
  topLeft?: ReactNode;
  topRight?: ReactNode;
  bottomRight?: ReactNode;
}) {
  return (
    <div className="relative aspect-[16/10] lg:aspect-auto lg:w-80 lg:h-full min-h-[256px] overflow-hidden flex-shrink-0">
      {image ? (
        <img
          src={image.url}
          alt={image.altText ?? title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted">
          <span className="text-sm text-muted-foreground text-center px-4">Ingen bilde</span>
        </div>
      )}
      {topLeft && <div className="absolute left-4 top-4 z-10">{topLeft}</div>}
      {topRight && <div className="absolute right-4 top-4 z-10">{topRight}</div>}
      {bottomRight && <div className="absolute right-4 bottom-4 z-10">{bottomRight}</div>}
    </div>
  );
}

// Vehicle details line (year, mileage, transmission, fuel)
export function vehicleDetailsString(v: AuctionVehicle | null | undefined): string {
  if (!v) return "";
  return [
    v.modelYear ? String(v.modelYear) : null,
    v.mileage != null ? formatMileage(v.mileage) : null,
    v.transmission ? normalizeTransmission(v.transmission) : null,
    v.fuelType ? normalizeFuelType(v.fuelType) : null,
  ].filter(Boolean).join(" \u00B7 ");
}

// Title + details header
export function AuctionCardHeader({
  title,
  details,
  bidCount,
  referenceNumber,
  registrationNumber,
  registrationBadgeClass,
  timingSlot,
}: {
  title: string;
  details: string;
  bidCount?: number;
  referenceNumber?: string;
  registrationNumber?: string | null;
  registrationBadgeClass?: string;
  timingSlot?: ReactNode;
}) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <h3 className="text-xl font-black leading-tight text-[#1a2e35]" style={{ fontFamily: "var(--font-display)" }}>
          {title}
        </h3>
        <p className="mt-1 text-sm font-medium text-muted-foreground">{details}</p>
      </div>
      <div className="flex flex-col items-end gap-1">
        {bidCount != null && (
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-accent uppercase tracking-widest">
            <Gavel className="h-4 w-4" />
            {bidCount} bud
          </div>
        )}
        {timingSlot && <div className="mt-1">{timingSlot}</div>}
        {registrationNumber && (
          <span className={`rounded-md px-2 py-0.5 text-[10px] font-mono font-bold border ${registrationBadgeClass ?? "bg-muted text-muted-foreground border-border"}`}>
            {registrationNumber}
          </span>
        )}
        {referenceNumber && (
          <span className="text-[10px] text-muted-foreground/60 font-mono">
            #{referenceNumber}
          </span>
        )}
      </div>
    </div>
  );
}

// Favorite button
export function FavoriteButton({
  isFavorite,
  onToggle,
}: {
  isFavorite: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-sm transition-all hover:scale-110 active:scale-90"
    >
      <Heart
        className="h-5 w-5"
        style={{
          fill: isFavorite ? "var(--accent)" : "transparent",
          color: isFavorite ? "var(--accent)" : "var(--primary)",
          opacity: isFavorite ? 1 : 0.6,
        }}
      />
    </button>
  );
}

// Flash auction badge
export function FlashBadge() {
  return (
    <div className="flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white shadow-[0_4px_12px_rgba(var(--accent-rgb),0.3)] ring-2 ring-white/20 animate-in zoom-in-50 duration-500">
      <Zap className="h-3.5 w-3.5 fill-current" />
      <span>Lynauksjon</span>
    </div>
  );
}

// Timing display for ended auctions
export function EndedTimingBadge({ closedAt, endsAt, createdAt, label }: {
  closedAt?: string | null;
  endsAt?: string | null;
  createdAt?: string | null;
  label?: string;
}) {
  const dateStr = closedAt || endsAt || createdAt;
  const prefix = label ?? (closedAt ? "Avsluttet" : endsAt ? "Avsluttet" : "Opprettet");

  return (
    <div className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-lg border border-border/10">
      <Clock className="h-3.5 w-3.5" />
      <span>
        {dateStr && !isNaN(new Date(dateStr).getTime())
          ? `${prefix}: ${new Date(dateStr).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "2-digit" })}`
          : "Avsluttet"}
      </span>
    </div>
  );
}

// Live timer wrapper
export function LiveTimingBadge({ endsAt }: { endsAt: string }) {
  return <LiveTimer endsAt={endsAt} className="shadow-none border-border/10" size="sm" />;
}

// Footer status banner (used by ManageAuctionCard)
export function FooterStatusBanner({ status, mode }: { status: string; mode: "seller" | "buyer" }) {
  const SELLER_FOOTERS: Record<string, { label: string; color: string }> = {
    pending_review: { label: "Venter på godkjenning fra administratør.", color: "text-blue-700 bg-blue-50 border-blue-100" },
    approved: { label: "Auksjonen er godkjent og klar til å startes.", color: "text-green-700 bg-green-50 border-green-100" },
    sold: { label: "Handelen er i boks! Kontrakt og faktura klargjøres.", color: "text-green-700 bg-green-50 border-green-100" },
    pending_seller_decision: { label: "Auksjonen avventer din beslutning.", color: "text-accent bg-accent/5 border-accent/10" },
    negotiating: { label: "Forhandling pågår \u2013 se detaljer for å svare på motbud.", color: "text-accent bg-accent/5 border-accent/10" },
    no_bids: { label: "Auksjonen fikk ingen bud. Velg om du vil relansere eller avslutte som ikke solgt.", color: "text-accent bg-accent/5 border-accent/10" },
    no_sale: { label: "Ingen salg denne gangen. Du kan re-liste bilen senere.", color: "text-muted-foreground bg-muted border-border" },
  };

  const BUYER_FOOTERS: Record<string, { label: string; color: string }> = {
    pending_seller_decision: { label: "Avventer svar fra selger \u2013 vi holder fingre og tær for deg!", color: "text-primary bg-primary/5 border-primary/10" },
    negotiating: { label: "Forhandling pågår \u2013 se detaljer for å svare på motbud.", color: "text-primary bg-primary/5 border-primary/10" },
    sold: { label: "Gratulerer med kjøpet! Vi klargjør kontrakten nå.", color: "text-blue-700 bg-blue-50 border-blue-100" },
    contract_pending: { label: "Kontrakten er på vei \u2013 sjekk innboksen og signer.", color: "text-blue-700 bg-blue-50 border-blue-100" },
    invoice_pending: { label: "Kontrakten er signert \u2013 vi klargjør fakturaen nå.", color: "text-green-700 bg-green-50 border-green-100" },
    payment_pending: { label: "Faktura er sendt \u2013 betal for å fullføre kjøpet.", color: "text-green-700 bg-green-50 border-green-100" },
    ready_for_pickup: { label: "Betaling mottatt \u2013 bilen er klar til henting!", color: "text-green-700 bg-green-50 border-green-100" },
    picked_up: { label: "Bilen er hentet fra selger og klargjøres for transport.", color: "text-blue-700 bg-blue-50 border-blue-100" },
    in_transit: { label: "Bilen er på vei til deg!", color: "text-blue-700 bg-blue-50 border-blue-100" },
    delivered: { label: "Bilen er levert \u2013 bekreft mottak eller meld fra om avvik.", color: "text-green-700 bg-green-50 border-green-100" },
  };

  const banners = mode === "seller" ? SELLER_FOOTERS : BUYER_FOOTERS;
  const b = banners[status];
  if (!b) return null;

  return (
    <div className={`rounded-xl px-4 py-2.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest border ${b.color}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      {b.label}
    </div>
  );
}
